import React, { useEffect, useState } from "react";
import { Button, Card, Empty, Input, Menu, message, Popconfirm, Table } from "antd";
import { DeleteOutlined, EditOutlined, PlusCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { useFirestore } from "react-redux-firebase";
import { useSelector } from "react-redux";
import EllipsisDropdown from "../../../components/shared-components/EllipsisDropdown";
import Flex from "../../../components/shared-components/Flex";
import { FIRESTORE_DEALERS_TABLE } from "../../../constants/FirestoreConstant";
import { useTranslation } from "react-i18next";
import Loading from "../../../components/shared-components/Loading";
import AddDealerModal from "./AddDealerModal";
import EditDealerModal from "./EditDealerModal";

const Dealers = () => {
  const { t } = useTranslation();
  const firestore = useFirestore();
  const [messageApi, contextHolder] = message.useMessage();

  // Get dealers from redux state
  const dealers = useSelector((state) => state.firestoreReducer.ordered.dealers);

  // Component states
  const [list, setList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);

  // Update lists when dealers data changes
  useEffect(() => {
    if (dealers) {
      setList(dealers);
      setIsLoading(false);
      onSearch(searchText, dealers);
    }
  }, [dealers]);

  // Search functionality
  const onSearch = (value, newList) => {
    setSearchText(value);
    const searchList = newList || list;

    if (!!value) {
      const filtered = searchList.filter(
        (dealer) =>
          (dealer.storeName && dealer.storeName.toLowerCase().includes(value.toLowerCase())) ||
          (dealer.city && dealer.city.toLowerCase().includes(value.toLowerCase())) ||
          (dealer.zipCode && dealer.zipCode.toLowerCase().includes(value.toLowerCase())),
      );
      setFilteredList(filtered);
    } else {
      setFilteredList(searchList);
    }
  };

  // Delete dealer function
  const deleteDealerConfirm = (record) => {
    firestore
      .collection(FIRESTORE_DEALERS_TABLE)
      .doc(record.id)
      .delete()
      .then(() => {
        messageApi.success(t("notifications.entry_updated"));
      })
      .catch((error) => {
        messageApi.error(error.message, 4);
      });
  };

  // Dropdown menu for actions
  const dropdownMenu = (row) => (
    <Menu>
      <Menu.Item onClick={() => openEditModal(row)}>
        <Flex alignItems="center">
          <EditOutlined />
          <span style={{ marginLeft: 4 }}>{t("edit")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item>
        <Popconfirm
          placement="bottom"
          title={t("confirm_delete_dealer", { name: row.storeName })}
          onConfirm={() => deleteDealerConfirm(row)}
          okText={t("yes")}
          cancelText={t("no")}
        >
          <a onClick={(e) => e.preventDefault()}>
            <DeleteOutlined />
            {t("delete")}
          </a>
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  // Modal functions
  const openAddModal = () => {
    setAddModalVisible(true);
  };

  const closeAddModal = () => {
    setAddModalVisible(false);
  };

  const openEditModal = (record) => {
    setSelectedDealer(record);
    setEditModalVisible(true);
  };

  const closeEditModal = () => {
    setEditModalVisible(false);
    setSelectedDealer(null);
  };

  // Table columns
  const tableColumns = [
    {
      title: t("dealer_name"),
      dataIndex: "storeName",
      sorter: (a, b) => (a.storeName || "").localeCompare(b.storeName || ""),
    },
    {
      title: t("address"),
      dataIndex: "street",
    },
    {
      title: t("zip_code"),
      dataIndex: "zipCode",
    },
    {
      title: t("city"),
      dataIndex: "city",
      sorter: (a, b) => (a.city || "").localeCompare(b.city || ""),
    },
    {
      title: t("country"),
      dataIndex: "country",
      sorter: (a, b) => (a.country || "").localeCompare(b.country || ""),
    },
    {
      title: t("premium"),
      dataIndex: "tag",
      render: (tag) => (tag === "Premium" ? t("yes") : t("no")),
      sorter: (a, b) => {
        // Sort by Premium status - Premium comes first
        const valueA = a.tag === "Premium" ? 1 : 0;
        const valueB = b.tag === "Premium" ? 1 : 0;
        return valueB - valueA; // Reverse order to show Premium first
      },
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <Input
            placeholder={t("search")}
            prefix={<SearchOutlined />}
            onChange={(e) => onSearch(e.target.value)}
            value={searchText}
            style={{ maxWidth: 300 }}
          />
          <Button type="primary" icon={<PlusCircleOutlined />} onClick={openAddModal}>
            {t("add_dealer")}
          </Button>
        </div>
        {isLoading ? (
          <Loading />
        ) : (
          <div className="table-responsive">
            <Table
              columns={tableColumns}
              dataSource={filteredList}
              rowKey="id"
              pagination={{ pageSize: 20 }}
              locale={{
                emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("table_no_dealers")} />,
              }}
              scroll={{ x: 1000 }}
            />
          </div>
        )}
      </Card>

      <AddDealerModal visible={addModalVisible} onCancel={closeAddModal} />

      {selectedDealer && (
        <EditDealerModal visible={editModalVisible} onCancel={closeEditModal} dealer={selectedDealer} />
      )}
    </>
  );
};

export default Dealers;
